/**
 * 저장 거절 안내 목록 — "어느 표 어느 셀이 왜 막혔는지"를 화면까지 가져온다.
 *
 * 거절 목록은 저장 시점의 스냅샷이라 그대로 두면 이미 고친 셀도 계속 남는다.
 * 현재 편집 상태(cellChanges)와 대조해 "아직 거절 당시 값 그대로인" 건만 살린다
 * — 수정하거나 원복한 셀은 목록에서 빠지고, 전부 빠지면 안내가 닫힌다.
 *
 * 위치를 찾지 못한 건(index 미매칭 등)도 목록에 남긴다. 셀로 이동만 못 할 뿐
 * "저장되지 않은 값이 N건"에서 조용히 빠지면 건수가 사용자를 속인다.
 *
 * 순서는 편집한 순서가 아니라 화면에 보이는 순서다 — 목차(chunk) → 표(dlView) →
 * 기간 섹션 → 행 → 열. 다음/이전 버튼이 화면을 위아래로 튀면 "어디를 보고 있는지"를
 * 사용자가 매번 다시 잡아야 한다.
 */

import type { FsType, IxdChunk } from "@/apis/ixd-editor/types";
import { getDisplayPeriodOrder } from "@/utils/ixd-editor/period-order";
import type { RejectedCellChange } from "@/hooks/ixd-edit-session";
import type { CellIdentityLocation, ChangeKey } from "@/utils/ixd-cell-model";

export interface RejectionGuideItem {
  changeKey: ChangeKey;
  /** 저장이 막힌 입력값 */
  value: string;
  /** 거절 사유 — 사용자가 무엇을 고쳐야 하는지 */
  reason: string;
  /** 셀 위치. 못 찾으면 null — 이동 버튼만 동작하지 않는다. */
  location: CellIdentityLocation | null;
  /** 표·항목 경로 한 줄 */
  where: string;
}

const UNKNOWN_LOCATION = "위치를 찾지 못한 셀";

const describeLocation = (location: CellIdentityLocation | null): string => {
  if (!location) return UNKNOWN_LOCATION;
  const parts = [location.chunkTitle, location.dlViewTitle, location.rowLabel]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));
  // 대목차 제목이 표 제목과 같은 경우가 흔하다 — 같은 말을 두 번 보여주지 않는다.
  return [...new Set(parts)].join(" · ") || UNKNOWN_LOCATION;
};

interface BuildRejectionGuideItemsInput {
  /** 마지막 저장의 거절 목록 */
  rejections: readonly RejectedCellChange[];
  /** 화면이 보여주는 fsType — 다른 쪽 거절은 이 화면에서 이동시킬 수 없다 */
  fsType: FsType;
  /** 현재 편집 상태(해당 fsType 슬라이스) */
  cellChanges: Record<ChangeKey, string>;
  findLocation: (changeKey: ChangeKey) => CellIdentityLocation | null;
  /** 화면 표시 순서의 chunk 목록 — 이 순서가 곧 세로 배치 순서다. */
  chunks: readonly IxdChunk[];
}

/**
 * 화면 배치 순서 정렬키. 위치를 못 찾은 건은 비교할 좌표가 없어 맨 뒤로 보낸다.
 * 기간 섹션 순서는 표마다 다르다 — 자본변동표는 전기가 당기보다 위다.
 */
const displayRank = (
  location: CellIdentityLocation | null,
  chunks: readonly IxdChunk[],
): number[] => {
  if (!location) return [Infinity, Infinity, Infinity, Infinity, Infinity];
  const chunkIndex = chunks.findIndex((chunk) => chunk.id === location.chunkId);
  const dlView = chunks[chunkIndex]?.dlViews[location.dlViewIndex];
  const periodIndex = getDisplayPeriodOrder(dlView?.fsType).indexOf(
    location.periodType,
  );
  return [
    chunkIndex < 0 ? Infinity : chunkIndex,
    location.dlViewIndex,
    periodIndex < 0 ? Infinity : periodIndex,
    location.rowIndex ?? 0,
    location.colIndex ?? 0,
  ];
};

const compareRanks = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
};

export const buildRejectionGuideItems = ({
  rejections,
  fsType,
  cellChanges,
  findLocation,
  chunks,
}: BuildRejectionGuideItemsInput): RejectionGuideItem[] =>
  rejections
    .flatMap((rejection) => {
      if (rejection.fsType !== fsType) return [];
      // 값이 바뀌었거나(수정) 사라졌으면(원복·저장) 이미 처리된 건이다.
      if (cellChanges[rejection.changeKey] !== rejection.value) return [];
      const location = findLocation(rejection.changeKey);
      return [
        {
          changeKey: rejection.changeKey,
          value: rejection.value,
          reason: rejection.reason,
          location,
          where: describeLocation(location),
        },
      ];
    })
    .sort((a, b) =>
      compareRanks(
        displayRank(a.location, chunks),
        displayRank(b.location, chunks),
      ),
    );
